'use client'

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { MagnifyingGlassIcon, SpinnerIcon } from '@phosphor-icons/react'
import Fuse from 'fuse.js'
import { Dialog, DialogContent, DialogTitle } from '@/ui/dialog'
import { useEmbeddingContext } from './use-embedding-context'
import { SearchResultRow } from './search-result-row'
import type { Entry } from '@/document'
import { useDocumentView } from '@/document'

function getEntryRoute(entry: Entry): string {
  if (entry.type === 'movie') return `/app/movies?expand=${entry.id}`
  if (entry.type === 'book') return `/app/books?expand=${entry.id}`
  if (
    entry.type === 'artist' ||
    entry.type === 'release' ||
    entry.type === 'label'
  ) {
    return `/app/music?expand=${entry.id}`
  }
  if (entry.type === 'person') return `/app/people?expand=${entry.id}`
  return `/app/journal?expand=${entry.id}`
}

interface SearchResult {
  entry: Entry
  score?: number
}

interface GlobalSearchModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GlobalSearchModal({
  open,
  onOpenChange,
}: GlobalSearchModalProps) {
  const router = useRouter()
  const embedding = useEmbeddingContext()
  const doc = useDocumentView()

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const entries = useMemo(() => {
    if (!doc?.entries) return []
    return Object.values(doc.entries).filter(
      (e): e is Entry => !!e && !e.archivedAt,
    )
  }, [doc])

  const fuseIndex = useMemo(
    () =>
      new Fuse(entries, {
        keys: ['title', 'content'],
        threshold: 0.4,
        includeScore: true,
      }),
    [entries],
  )

  const entriesById = useMemo(() => {
    const map = new Map<string, Entry>()
    for (const entry of entries) {
      map.set(entry.id, entry)
    }
    return map
  }, [entries])

  // Stable reference to whether semantic search is available
  const semanticReady = embedding !== null && embedding.modelReady
  const semanticReadyRef = useRef(semanticReady)
  const embeddingRef = useRef(embedding)
  const entriesByIdRef = useRef(entriesById)

  // Sync refs in effect to avoid "Cannot update ref during render" lint error
  useEffect(() => {
    semanticReadyRef.current = semanticReady
    embeddingRef.current = embedding
    entriesByIdRef.current = entriesById
  })

  // Debounced search — only re-run on query or open change, not embedding progress
  useEffect(() => {
    if (!open) return

    if (!query.trim()) {
      // Intentional sync reset — clearing results on empty query is not a cascading render
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults([])

      setSelectedIndex(0)
      return
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(async () => {
      const emb = embeddingRef.current
      if (semanticReadyRef.current && emb) {
        const hits = await emb.search(query, 20)
        const mapped: SearchResult[] = []
        for (const hit of hits) {
          const entry = entriesByIdRef.current.get(hit.entryId)
          if (entry) {
            mapped.push({ entry, score: hit.cosineScore })
          }
        }
        setResults(mapped)
      } else {
        const fuseResults = fuseIndex.search(query, { limit: 20 })
        setResults(
          fuseResults.map((r) => ({ entry: r.item, score: undefined })),
        )
      }
      setSelectedIndex(0)
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, open, fuseIndex])

  // Reset on close — intentional sync state clear, not a cascading render concern
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQuery('')

      setResults([])

      setSelectedIndex(0)
    }
  }, [open])

  const handleSelect = useCallback(
    (entry: Entry) => {
      const route = getEntryRoute(entry)
      router.push(route)
      onOpenChange(false)
    },
    [router, onOpenChange],
  )

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return
    const selected = listRef.current.children[selectedIndex] as
      | HTMLElement
      | undefined
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          setSelectedIndex((i) => Math.min(i + 1, results.length - 1))
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          setSelectedIndex((i) => Math.max(i - 1, 0))
          break
        }
        case 'Enter': {
          e.preventDefault()
          const result = results[selectedIndex]
          if (result) {
            handleSelect(result.entry)
          }
          break
        }
      }
    },
    [results, selectedIndex, handleSelect],
  )

  const modelLoading = embedding !== null && embedding.modelLoading
  const modelProgress = embedding?.modelProgress ?? 0
  const indexingStatus = embedding?.indexingStatus ?? 'idle'
  const indexedCount = embedding?.indexedCount ?? 0
  const totalCount = embedding?.totalCount ?? 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="top-[20%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-xl"
        onKeyDown={handleKeyDown}
      >
        <DialogTitle className="sr-only">Search</DialogTitle>

        {/* Search input */}
        <div className="border-b-foreground/10 flex items-center gap-3 border-b px-4 py-3">
          <MagnifyingGlassIcon className="text-muted-foreground h-5 w-5 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search everything..."
            className="text-foreground placeholder:text-muted-foreground flex-1 bg-transparent text-sm outline-none"
            autoFocus
          />
          {semanticReady && (
            <span className="text-muted-foreground bg-muted/80 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium">
              Smart search
            </span>
          )}
          {modelLoading && (
            <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-[10px]">
              <SpinnerIcon className="h-3 w-3 animate-spin" />
              Loading AI model
              {modelProgress > 0 ? ` ${Math.round(modelProgress)}%` : '...'}
            </span>
          )}
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="max-h-[min(400px,60vh)] overflow-y-auto p-1"
        >
          {query.trim() && results.length === 0 && (
            <div className="text-muted-foreground py-8 text-center text-sm">
              No results
            </div>
          )}
          {results.map((result, i) => (
            <SearchResultRow
              key={result.entry.id}
              entry={result.entry}
              score={result.score}
              selected={i === selectedIndex}
              onClick={() => handleSelect(result.entry)}
            />
          ))}
        </div>

        {/* Footer */}
        {entries.length > 0 && (
          <div className="border-t-foreground/10 flex items-center justify-between border-t px-4 py-2">
            <span className="text-muted-foreground/60 text-[10px]">
              {indexingStatus === 'indexing'
                ? `Indexing ${indexedCount}/${totalCount} entries...`
                : semanticReady
                  ? `Searching ${entries.length} entries with AI`
                  : `Searching ${entries.length} entries`}
            </span>
            <div className="text-muted-foreground/40 flex gap-2 text-[10px]">
              <kbd className="bg-muted/60 rounded px-1.5 py-0.5 font-mono">
                ↑↓
              </kbd>
              <span>navigate</span>
              <kbd className="bg-muted/60 rounded px-1.5 py-0.5 font-mono">
                ↵
              </kbd>
              <span>open</span>
              <kbd className="bg-muted/60 rounded px-1.5 py-0.5 font-mono">
                esc
              </kbd>
              <span>close</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
